import isEmpty from 'lodash-es/isEmpty'
import { FC, useCallback, useMemo, useRef } from 'react'
import styled from 'styled-components'

import { Button as LMFButton, TypeStyle, fromMd, untilMd } from '@syconium/little-miss-figgy'

import { useLocalization } from '../../../../app/_providers/LocalizationProvider.client'
import { usePortal } from '../../../../app/_providers/PortalProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { EmbroideryService } from '../../../../containers/EmbroideryService'
import { EmbroideryModalService } from '../../../../containers/EmbroideryService/EmbroideryModalService'
import { trackEvent } from '../../../../lib/analytics'
import { intlPrettyCurrency } from '../../../../lib/utils'
import { Embroidery } from '../../../../types/graphql'

import { EmbroideryForm } from './EmbroideryForm'
import { EmbroideryFormHeader } from './EmbroideryFormHeader'
import { EmbroideryOptionList } from './EmbroideryOptionList'

export const ModalEmbroideryOptions: FC<{
  embroideryOptions: ReadonlyArray<Embroidery>
  heapCtaAction?: string
  handleBackButtonClick: () => void
}> = ({ embroideryOptions, heapCtaAction, handleBackButtonClick }) => {
  const {
    clearSelectedEmbroideryFormOptions,
    currentlySelectedFormOption,
    hideFields,
    selectedEmbroideryOptions,
    selectedFormKind,
  } = EmbroideryService.useContainer()

  const { portal } = usePortal()
  const { handleConfirm } = EmbroideryModalService.useContainer()
  const { locale, region } = useLocalization()
  const {
    embroidery: { modal: strings, embroideryOptionTitles, discountText },
  } = useTranslation()

  const headerPrice = currentlySelectedFormOption?.variants[0]?.priceDetails.price
    ? intlPrettyCurrency({
        unit: 'cents',
        value: currentlySelectedFormOption?.variants[0]?.priceDetails.price.amount,
        region: region,
        locale: locale,
      })
    : undefined

  const headerStep = useMemo(() => {
    switch (selectedFormKind) {
      case 'LOGO_SETUP':
        return '1/2'
      case 'ADD_LOGO':
        return '2/2'
      default:
        return null
    }
  }, [selectedFormKind])
  const optionKey =
    `ADD_${currentlySelectedFormOption?.kind}` as keyof typeof embroideryOptionTitles
  const formTitle = currentlySelectedFormOption
    ? embroideryOptionTitles[optionKey]
    : strings.optionsTitle

  const portalDiscountPercentage = portal?.embroidery?.embroideryDiscountPercentage
  const portalDiscountMessage = useMemo(() => {
    // Only display portal discount messaging if the portal-specific logo embroidery option is present
    const hasPortalEmbroideryOptions = embroideryOptions.some(({ kind }) => kind === 'LOGO_ONLY')
    if (!portalDiscountPercentage || !hasPortalEmbroideryOptions) return null

    return discountText.formSection.replace(
      '{{discountPercentage}}',
      portalDiscountPercentage.toString()
    )
  }, [discountText.formSection, portalDiscountPercentage, embroideryOptions])

  const confirmDisabled = isEmpty(selectedEmbroideryOptions)

  const formHeaderTitleRef = useRef<HTMLHeadingElement>(null)
  const handleResetClick = useCallback(() => {
    clearSelectedEmbroideryFormOptions()
    if (formHeaderTitleRef.current) {
      formHeaderTitleRef.current.focus()
    }
  }, [clearSelectedEmbroideryFormOptions])

  return (
    <Container>
      <EmbroideryFormHeader.Wrapper>
        {selectedFormKind ? (
          <EmbroideryFormHeader.BackButton
            text={strings.backButton}
            handleClick={handleBackButtonClick}
          />
        ) : null}
        <EmbroideryFormHeader.Title ref={formHeaderTitleRef}>
          {formTitle}
        </EmbroideryFormHeader.Title>
        {headerStep ? (
          <EmbroideryFormHeader.Step step={headerStep} />
        ) : headerPrice ? (
          <EmbroideryFormHeader.Price price={headerPrice} />
        ) : null}
      </EmbroideryFormHeader.Wrapper>

      <>
        {selectedFormKind ? (
          <FormContent>
            <EmbroideryForm
              formKind={selectedFormKind}
              embroideryOption={currentlySelectedFormOption!}
              heapCtaAction={heapCtaAction}
              hideFields={hideFields?.[selectedFormKind]}
            />
          </FormContent>
        ) : (
          <Content>
            <Title>{strings.previewTitle}</Title>
            <EmbroideryOptionList embroideryOptions={embroideryOptions} />
            {/* portal-specific discount messaging */}
            <ButtonWrap>
              <Button
                aria-label={strings.confirm}
                onClick={confirmDisabled ? undefined : handleConfirm}
                aria-disabled={confirmDisabled}
                {...trackEvent({ category: 'embroidery', action: 'confirm embroidery' })}
              >
                {strings.confirm}
              </Button>
              {!confirmDisabled && (
                <ResetButton
                  aria-label={strings.forms.submissionButtons.resetCta}
                  variant='black-on-white'
                  onClick={handleResetClick}
                >
                  {strings.forms.submissionButtons.resetCta}
                </ResetButton>
              )}
            </ButtonWrap>
            {portalDiscountMessage && (
              <DiscountMessagingText>{portalDiscountMessage}</DiscountMessagingText>
            )}
          </Content>
        )}
      </>
    </Container>
  )
}

const Container = styled.div`
  background-color: #fff;
  width: 100%;

  ${fromMd} {
    border: 1px solid ${o => o.theme.color.deprecated.stoneLight};
    border-radius: ${o => o.theme.spacing(2)};
    max-width: 468px;
  }
`

const Content = styled.div`
  padding: 0 ${o => o.theme.spacing(4)};

  ${fromMd} {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow-y: auto;
    min-height: 0px;
    padding: 0 ${o => o.theme.spacing(6)} ${o => o.theme.spacing(6)} ${o => o.theme.spacing(6)};
  }
`

const FormContent = styled(Content)`
  ${fromMd} {
    overflow-x: hidden;
  }
  ${untilMd} {
    padding: 0;
  }
`
const ButtonWrap = styled.div`
  padding: ${o => o.theme.spacing(4)} 0 ${o => o.theme.spacing(6)} 0;
  ${fromMd} {
    padding: ${o => o.theme.spacing(6)} 0 0 0;
  }
`

const Button = styled(LMFButton)`
  width: 100%;
  padding: ${o => o.theme.spacing(4)} ${o => o.theme.spacing(8)};

  &[aria-disabled='true'] {
    opacity: 0.7;
    background-color: ${o => o.theme.color.deprecated.stoneLight};
    border-color: ${o => o.theme.color.deprecated.stoneLight};
    color: ${o => o.theme.color.deprecated.obsidianLight};
  }
`

const ResetButton = styled(Button)`
  margin-top: ${o => o.theme.spacing(2)};
`

const Title = styled.div`
  ${TypeStyle.css.headlineExtraSmall}
  padding: ${o => o.theme.spacing(5)} 0;

  ${fromMd} {
    display: none;
  }
`

const DiscountMessagingText = styled(TypeStyle.HeadlineExtraExtraSmall).attrs({ asTag: 'span' })`
  display: block;
  color: ${o => o.theme.color.deprecated.obsidian};
  margin-top: ${o => o.theme.spacing(6)};
  text-transform: uppercase;
  text-align: center;
`
